import React, { useState, useEffect } from 'react';
import authenticationService from '../../services/authenticationService';

const SessionManagement = ({ scopeType, scopeId }) => {
  const [policy, setPolicy] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    loadPolicy();
    loadSessions();
  }, [scopeType, scopeId]);

  const loadPolicy = async () => {
    try {
      setLoading(true);
      const data = await authenticationService.getPolicy(scopeType, scopeId);
      setPolicy(data.sessionPolicy || {});
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadSessions = async () => {
    try {
      const data = await authenticationService.getActiveSessions();
      setSessions(data || []);
    } catch (err) {
      console.error('Failed to load sessions:', err);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      await authenticationService.updateSessionPolicy(scopeType, scopeId, policy);
      setSuccess('Session policy updated successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRevokeSession = async (sessionId) => {
    if (!window.confirm('Are you sure you want to revoke this session?')) {
      return;
    }

    try {
      await authenticationService.revokeSession(sessionId);
      setSuccess('Session revoked successfully');
      setTimeout(() => setSuccess(null), 3000);
      loadSessions();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRevokeAll = async () => {
    if (!window.confirm('Are you sure you want to revoke all sessions? You will be logged out.')) {
      return;
    }

    try {
      await authenticationService.revokeAllSessions();
      setSuccess('All sessions revoked successfully');
      setTimeout(() => {
        setSuccess(null);
        window.location.href = '/login';
      }, 2000);
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>;
  }

  if (!policy) {
    return <div>Failed to load session policy</div>;
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <div>
      {/* Error/Success messages */}
      {error && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          color: '#991b1b',
          marginBottom: '16px'
        }}>
          {error}
        </div>
      )}

      {success && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#d1fae5',
          border: '1px solid #6ee7b7',
          borderRadius: '8px',
          color: '#065f46',
          marginBottom: '16px'
        }}>
          {success}
        </div>
      )}

      {/* Policy Form */}
      <div style={{
        backgroundColor: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        padding: '24px',
        marginBottom: '24px'
      }}>
        <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '20px' }}>
          Session Policy Settings
        </h3>

        <div style={{ display: 'grid', gap: '24px' }}>
          {/* Desktop Timeouts */}
          <div style={{ borderBottom: '1px solid #e5e7eb', paddingBottom: '20px' }}>
            <h4 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>Desktop Session Timeouts</h4>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
                Idle Timeout (minutes)
              </label>
              <input
                type="number"
                min={5}
                max={1440}
                value={policy.idleTimeoutMinutes || 60}
                onChange={(e) => setPolicy({ ...policy, idleTimeoutMinutes: parseInt(e.target.value) || 60 })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
              <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                Users will be logged out after {policy.idleTimeoutMinutes || 60} minutes of inactivity
              </p>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
                Absolute Timeout (minutes)
              </label>
              <input
                type="number"
                min={60}
                max={10080}
                value={policy.absoluteTimeoutMinutes || 480}
                onChange={(e) => setPolicy({ ...policy, absoluteTimeoutMinutes: parseInt(e.target.value) || 480 })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
              <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                Maximum session length: {Math.floor((policy.absoluteTimeoutMinutes || 480) / 60)} hours
              </p>
            </div>
          </div>

          {/* Mobile Timeouts */}
          <div style={{ borderBottom: '1px solid #e5e7eb', paddingBottom: '20px' }}>
            <h4 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>Mobile Session Timeouts</h4>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
                Idle Timeout (minutes)
              </label>
              <input
                type="number"
                min={5}
                max={1440}
                value={policy.mobileIdleTimeoutMinutes || 120}
                onChange={(e) => setPolicy({ ...policy, mobileIdleTimeoutMinutes: parseInt(e.target.value) || 120 })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
                Absolute Timeout (minutes)
              </label>
              <input
                type="number"
                min={60}
                max={10080}
                value={policy.mobileAbsoluteTimeoutMinutes || 1440}
                onChange={(e) => setPolicy({ ...policy, mobileAbsoluteTimeoutMinutes: parseInt(e.target.value) || 1440 })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>

          {/* Concurrent Sessions */}
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
              Maximum Concurrent Sessions
            </label>
            <input
              type="number"
              min={1}
              max={20}
              value={policy.maxConcurrentSessions || 5}
              onChange={(e) => setPolicy({ ...policy, maxConcurrentSessions: parseInt(e.target.value) || 5 })}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
            <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
              Maximum number of active sessions per user
            </p>
          </div>

          {/* Security Options */}
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '12px' }}>
              Security Options
            </label>
            <div style={{ display: 'grid', gap: '12px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={policy.requireReauthForSensitiveActions !== false}
                  onChange={(e) => setPolicy({ ...policy, requireReauthForSensitiveActions: e.target.checked })}
                  style={{ width: '18px', height: '18px', marginRight: '8px' }}
                />
                <span style={{ fontSize: '14px' }}>Require re-authentication for sensitive actions</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={policy.ipValidationEnabled || false}
                  onChange={(e) => setPolicy({ ...policy, ipValidationEnabled: e.target.checked })}
                  style={{ width: '18px', height: '18px', marginRight: '8px' }}
                />
                <span style={{ fontSize: '14px' }}>Enable IP address validation (alert on IP change)</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={policy.rememberMeEnabled !== false}
                  onChange={(e) => setPolicy({ ...policy, rememberMeEnabled: e.target.checked })}
                  style={{ width: '18px', height: '18px', marginRight: '8px' }}
                />
                <span style={{ fontSize: '14px' }}>Enable "Remember Me" option</span>
              </label>
            </div>
          </div>

          {/* Remember Me Duration */}
          {policy.rememberMeEnabled !== false && (
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
                Remember Me Duration (days)
              </label>
              <input
                type="number"
                min={1}
                max={365}
                value={policy.rememberMeMaxDays || 30}
                onChange={(e) => setPolicy({ ...policy, rememberMeMaxDays: parseInt(e.target.value) || 30 })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>
          )}
        </div>

        {/* Save Button */}
        <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '10px 24px',
              backgroundColor: '#3b82f6',
              color: '#ffffff',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.6 : 1
            }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Active Sessions */}
      <div style={{
        backgroundColor: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        padding: '24px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 600 }}>Active Sessions</h3>
          {sessions.length > 0 && (
            <button
              onClick={handleRevokeAll}
              style={{
                padding: '8px 16px',
                backgroundColor: '#ef4444',
                color: '#ffffff',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer'
              }}
            >
              Revoke All Sessions
            </button>
          )}
        </div>

        {sessions.length === 0 ? (
          <p style={{ color: '#6b7280', fontSize: '14px' }}>No active sessions</p>
        ) : (
          <div style={{ display: 'grid', gap: '12px' }}>
            {sessions.map(session => (
              <div
                key={session.id}
                style={{
                  padding: '16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
                    {session.deviceInfo || 'Unknown Device'}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>
                    IP: {session.ipAddress || 'Unknown'} • Last active: {formatDate(session.lastActivityAt)}
                  </div>
                </div>
                <button
                  onClick={() => handleRevokeSession(session.id)}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: '#fef2f2',
                    color: '#991b1b',
                    border: '1px solid #fecaca',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: 500,
                    cursor: 'pointer'
                  }}
                >
                  Revoke
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SessionManagement;

